import { Box, SxProps, Tooltip, TooltipProps } from '@mui/material';
import { t } from 'i18next';
import { SEVERITY_COLORS, Severity, getLabel } from '../../../common/enums/Severity';
import { IssueStatistics } from '../models/valueObjects/IssueStatistics';

type Props = {
  filter: Map<Severity, boolean>;
  stats: IssueStatistics;
};

// For the UI effect: instead of display: none and visibility: hidden, we are
// collapsing component so that we can use the transform for the smooth transition.
const emptyStyle = {
  width: 0,
  overflow: 'hidden',
  flex: 0,
  px: 0,
};

const containerStyle: SxProps = {
  display: 'flex',
  backgroundColor: 'backdrops.issues',
  flexGrow: 1,
  height: '1.75rem',
  borderRadius: '4px',
  overflow: 'hidden',
  cursor: 'pointer',
};

const tooltipProps: Partial<TooltipProps> = {
  arrow: true,
  placement: 'top',
  role: 'tooltip',
};

/**
 * Bar for issues and their ratios.
 * Given the filter, box will be displayed/hidden.
 */
export const IssueBar = ({ filter, stats }: Props) => {
  const blocks = [
    { severity: Severity.HIGH, value: stats.getNumberOfHigh() },
    { severity: Severity.MED, value: stats.getNumberOfMed() },
    { severity: Severity.LOW, value: stats.getNumberOfLow() },
    { severity: Severity.NO_WARNING, value: stats.getNumberOfNoWarning() },
  ];

  const hasAnyIssues = blocks.map((b) => b.value).some((v) => v !== 0);

  if (!hasAnyIssues) {
    return (
      <Tooltip {...tooltipProps} title={t('a11y.organization.projects.issueBarEmpty')}>
        <Box sx={containerStyle} />
      </Tooltip>
    );
  }

  return (
    <Box sx={containerStyle}>
      {blocks.map(({ severity, value }) => {
        let additionalStyle: SxProps = {};
        if (!filter.get(severity) || value === 0) {
          additionalStyle = emptyStyle;
        } else if (severity !== Severity.NO_WARNING) {
          additionalStyle = {
            borderRight: '1px solid',
          };
        }

        return (
          <Tooltip
            key={severity}
            {...tooltipProps}
            title={
              severity === Severity.NO_WARNING
                ? t('a11y.organization.projects.issueBarElementNoWarning', { amount: value })
                : t('a11y.organization.projects.issueBarElement', { severity: getLabel(severity).toLocaleLowerCase(), amount: value })
            }
          >
            <Box
              sx={{
                transition: '0.3s ease-in-out all',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                fontWeight: 500,
                px: 1,
                flex: value || 1,
                ...additionalStyle,
                color: SEVERITY_COLORS[severity].filledText,
                backgroundColor: SEVERITY_COLORS[severity].main,
                borderRightColor: 'white',
              }}
            >
              {value}
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );
};
